/**
 * MUTAGIM.SHOP — Admin Emails Settings Page (Phase 9)
 * Configuration des templates et séquences d'emails
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Mail, Settings, Play, Pause, Clock, Send, Eye, 
  CheckCircle, AlertCircle, Loader2, ChevronRight 
, ArrowLeft } from 'lucide-react';
import { AdminPageWrapper } from '@/components/admin/AdminPageWrapper';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminEmailsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [sequences, setSequences] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [settingsRes, sequencesRes, templatesRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/emails/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/emails/sequences`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/emails/templates`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
      ]);

      if (settingsRes.ok) {
        setSettings(await settingsRes.json());
      }
      if (sequencesRes.ok) {
        const data = await sequencesRes.json();
        setSequences(data.sequences || []);
      }
      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching email settings:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const updateSequence = async (sequenceId, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/emails/sequences/${sequenceId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        setSequences(prev => prev.map(seq => 
          seq.id === sequenceId ? { ...seq, ...updates } : seq
        ));
        toast.success('Séquence mise à jour');
      }
    } catch (error) {
      console.error('Error updating sequence:', error);
      toast.error('Erreur de mise à jour');
    }
  };

  const updateSettings = async (updates) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/emails/settings`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        setSettings(prev => ({ ...prev, ...updates }));
        toast.success('Paramètres mis à jour');
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Erreur de mise à jour');
    }
  };

  const handlePreviewTemplate = async (templateKey) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/emails/templates/${templateKey}/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewHtml(data.html);
        setPreviewTemplate(templateKey);
      }
    } catch (error) {
      console.error('Error previewing template:', error);
      toast.error('Erreur de prévisualisation');
    }
  };

  const sendTestEmail = async (templateKey) => {
    if (!testEmail) {
      toast.error('Entrez une adresse email');
      return;
    }

    setSendingTest(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/emails/send-test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          template_key: templateKey,
          recipient_email: testEmail,
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(`Email de test envoyé à ${testEmail}`);
      } else {
        toast.error(data.detail || 'Erreur d\'envoi');
      }
    } catch (error) {
      console.error('Error sending test:', error);
      toast.error('Erreur d\'envoi');
    } finally {
      setSendingTest(false);
    }
  };

  const formatDelay = (minutes) => {
    if (minutes === 0) return 'Immédiat';
    if (minutes < 60) return `${minutes} min`;
    if (minutes < 1440) return `${Math.round(minutes / 60)}h`;
    return `J+${Math.round(minutes / 1440)}`;
  };

  if (loading) {
    return (
      <AdminPageWrapper title="Emails & Automation" subtitle="Configuration des emails">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-mutagim-red" />
        </div>
      </AdminPageWrapper>
    );
  }

  return (
    <AdminPageWrapper title="Emails & Automation" subtitle="Configuration des emails">
      <div className="space-y-8" data-testid="admin-emails-page">
        {/* Action buttons */}
        <div className="flex justify-end">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/emails/queue')}
          >
            Voir la queue <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {/* Settings globaux */}
        <section className="bg-gray-900 rounded-lg p-6 border border-gray-800">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5" /> Paramètres généraux
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Mode Resend */}
            <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg">
              <div>
                <p className="text-white font-medium">Envoi réel (Resend)</p>
                <p className="text-sm text-gray-200">
                  {settings?.resend_enabled ? 'Actif' : 'Mode LOG (simulation)'}
                </p>
              </div>
              <Switch
                checked={settings?.resend_enabled || false}
                onCheckedChange={(checked) => updateSettings({ resend_enabled: checked })}
              />
            </div>

            {/* Limite anti-spam */}
            <div className="p-4 bg-gray-800 rounded-lg">
              <label className="block text-white font-medium mb-2">
                Limite anti-spam (emails/semaine)
              </label>
              <Input
                type="number"
                value={settings?.max_emails_per_week || 10}
                onChange={(e) => updateSettings({ max_emails_per_week: parseInt(e.target.value) })}
                className="bg-gray-700 border-gray-600 text-white w-24"
                min={1}
                max={50}
              />
            </div>
          </div>
        </section>

        {/* Séquences */}
        <section className="bg-gray-900 rounded-lg p-6 border border-gray-800">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Play className="w-5 h-5" /> Séquences automatiques
          </h2>
          
          <div className="space-y-3">
            {sequences.map((seq) => (
              <div 
                key={seq.id}
                className={cn(
                  "flex items-center justify-between p-4 rounded-lg border transition-colors",
                  seq.enabled 
                    ? "bg-gray-800/50 border-green-500/30" 
                    : "bg-gray-800/30 border-gray-700"
                )}
              >
                <div className="flex items-center gap-4">
                  <Switch
                    checked={seq.enabled}
                    onCheckedChange={(checked) => updateSequence(seq.id, { enabled: checked })}
                  />
                  <div>
                    <p className="text-white font-medium">{seq.name}</p>
                    <p className="text-sm text-gray-200">{seq.description}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-sm text-gray-200">Délai</p>
                    <p className="text-white font-mono">{formatDelay(seq.delay_minutes)}</p>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handlePreviewTemplate(seq.template)}
                    className="text-gray-200 hover:text-white"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Templates */}
        <section className="bg-gray-900 rounded-lg p-6 border border-gray-800">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5" /> Templates disponibles
          </h2>

          {/* Email de test */}
          <div className="mb-6 p-4 bg-gray-800 rounded-lg flex items-center gap-4">
            <Input
              type="email"
              placeholder="Email pour les tests..."
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="bg-gray-700 border-gray-600 text-white flex-1"
            />
            <span className="text-gray-200 text-sm">→ Sélectionnez un template ci-dessous</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {templates.map((template) => (
              <div 
                key={template.key}
                className="p-4 bg-gray-800 rounded-lg border border-gray-700 hover:border-mutagim-red/50 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-white font-medium">{template.name}</h3>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePreviewTemplate(template.key)}
                      className="text-gray-200 hover:text-white"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => sendTestEmail(template.key)}
                      disabled={sendingTest || !testEmail}
                      className="text-gray-200 hover:text-mutagim-red"
                    >
                      {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-gray-200 truncate">{template.subject}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Preview Modal */}
        {previewHtml && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-gray-800">
                <h3 className="text-white font-medium">
                  Prévisualisation: {templates.find(t => t.key === previewTemplate)?.name}
                </h3>
                <Button
                  variant="ghost"
                  onClick={() => { setPreviewHtml(null); setPreviewTemplate(null); }}
                  className="text-gray-200"
                >
                  Fermer
                </Button>
              </div>
              <div className="overflow-auto max-h-[80vh]">
                <iframe
                  srcDoc={previewHtml}
                  className="w-full h-[600px] bg-white"
                  title="Email Preview"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminPageWrapper>
  );
}

export default AdminEmailsPage;
